import React from 'react'
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { DraftCardInfo } from './hooks/useCopilotSession'
import { CopilotDraftHost } from './types'
import { revealCondition } from './utils/revealCondition'

interface CopilotDraftCardProps {
  card: DraftCardInfo
  toolCallId: string
  host: CopilotDraftHost
}

/** System card rendered under a draft-returning tool call: what changed, jump
 * to it, undo it. In-memory undo only — version history is the durable layer. */
const CopilotDraftCard: React.FC<CopilotDraftCardProps> = ({ card, toolCallId, host }) => {
  if (card.status === 'superseded' || card.status === 'discarded') {
    return (
      <Typography variant="caption" color="text.secondary">
        {card.status === 'discarded'
          ? 'Discarded — you kept your edits'
          : 'Superseded by a newer draft in the same turn'}
      </Typography>
    )
  }
  if (card.status === 'rejected') {
    return (
      <Alert severity="error" sx={{ py: 0 }}>
        {card.summary}
        {card.detail ? ` — ${card.detail}` : ''}
      </Alert>
    )
  }
  if (card.status === 'held') {
    return (
      <Alert severity="info" sx={{ py: 0 }}>
        {card.summary} — held because you edited the pattern mid-turn (see banner above)
      </Alert>
    )
  }
  return (
    <Box
      sx={{
        backgroundColor: 'background.custom3',
        border: '1px solid',
        borderColor: 'background.custom4',
        borderRadius: 1.5,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box display="flex" alignItems="center" gap={0.75}>
        <SmartToyOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {card.summary}
        </Typography>
      </Box>
      {card.diffSummary && (
        <Typography variant="caption" color="text.secondary">
          {card.diffSummary}
        </Typography>
      )}
      {card.warnings.length > 0 && (
        <Typography variant="caption" color="warning.main">
          {card.warnings.length} incomplete condition{card.warnings.length === 1 ? '' : 's'} — the
          pattern saves once they're finished
        </Typography>
      )}
      <Box display="flex" gap={1}>
        {card.firstAddedConditionId && (
          <Button size="small" onClick={() => revealCondition(card.firstAddedConditionId!)}>
            Show changes
          </Button>
        )}
        {/* Undo is strictly LIFO — only the latest live apply offers it */}
        {host.lastAppliedToolCallId === toolCallId && (
          <Tooltip title="Reverts this AI change — including any edits you made after it">
            <Button size="small" onClick={() => host.revertLastApply()}>
              Undo
            </Button>
          </Tooltip>
        )}
        {/* Redo mirrors it: only the just-undone apply offers it, until the
         * next apply supersedes the slot */}
        {host.lastRevertedToolCallId === toolCallId && (
          <Tooltip title="Restores the undone AI change — anything changed since the undo is replaced">
            <Button size="small" onClick={() => host.redoLastRevert()}>
              Redo
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

export default CopilotDraftCard
